import BigNumber from 'bignumber.js';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDebouncedBuilderValues } from '~/hooks/document-builder/useDebouncedBuilderValues';
import { useLineTotals } from '~/hooks/useLineTotals';
import { WithholdingTaxType } from '~/types/document';
import { DocumentBuilderForm, TaxableMode } from '~/types/document-builder';
import {
  calculateCompetencies,
  calculateVatItemsTotals,
  filterContributionBaseItems,
  findMaxVat,
} from '~/utils/invoicing/items';
import { withholdingTaxable } from '~/utils/invoicing/withholding';
import { useWithholdingsFieldArray } from '~/views/invoices/builder/hooks/fieldArrayContext';

export const useWithholdingTaxable = (): BigNumber => {
  const lineTotals = useLineTotals();
  const values = useDebouncedBuilderValues();
  const taxableItems = filterContributionBaseItems(values.items, values.fiscalRegime);
  const linesTotals = taxableItems.map(lineTotals);
  const vatItemsTotals = calculateVatItemsTotals(values.items, values.currency, values.useGrossPrices);
  const netTotalAmount = calculateCompetencies(linesTotals, values.useGrossPrices, vatItemsTotals);
  const maxVatAmount = findMaxVat(taxableItems);
  const taxable = withholdingTaxable(netTotalAmount, values.socialSecurity, maxVatAmount);
  const taxableNumber = taxable.toNumber();
  return useMemo(() => BigNumber(taxableNumber), [taxableNumber]);
};

export const useAddWithholding = () => {
  const { append } = useWithholdingsFieldArray();
  const { getValues } = useFormContext<DocumentBuilderForm>();
  const lineTotals = useLineTotals();

  return useCallback(() => {
    const values = getValues();
    const taxableItems = filterContributionBaseItems(values.items, values.fiscalRegime);
    const linesTotals = taxableItems.map(lineTotals);
    const vatItemsTotals = calculateVatItemsTotals(values.items, values.currency, values.useGrossPrices);
    const netTotalAmount = calculateCompetencies(linesTotals, values.useGrossPrices, vatItemsTotals);
    const maxVatAmount = findMaxVat(taxableItems);
    const totalToWithhold = withholdingTaxable(netTotalAmount, values.socialSecurity, maxVatAmount);

    append({
      amountPercentage: '100',
      taxableAmount: totalToWithhold.toFixed(),
      motivation: 'A',
      type: WithholdingTaxType.PersoneFisiche,
      percentage: '20',
      taxableMode: TaxableMode.Percentage,
    });
  }, [append, getValues, lineTotals]);
};
